import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';

interface PaginationComponentProps {
  currentPage: number; // Current active page (0-based for API)
  totalPages: number; // Total number of pages
  onPageChange: (page: number) => void; // Callback when a page is clicked
}

const PaginationComponent = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationComponentProps) => {
  // Generate page numbers to display (max 5 visible)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show current page and surrounding pages
      let start = Math.max(0, currentPage - 2);
      let end = Math.min(totalPages - 1, start + maxVisible - 1);

      // Adjust start if we're near the end
      if (end - start < maxVisible - 1) {
        start = Math.max(0, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center gap-1">
      {/* Previous Page Button */}
      <button
        className={cn(
          'p-1 rounded hover:bg-gray-100',
          currentPage === 0 && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => {
          if (currentPage > 0) {
            onPageChange(currentPage - 1);
          }
        }}
        disabled={currentPage === 0}
      >
        <ChevronLeft size={20} />
      </button>

      {/* Show first page if not in view */}
      {pageNumbers[0] > 0 && (
        <>
          <button
            className="px-3 py-1 rounded hover:bg-gray-100 text-sm"
            onClick={() => onPageChange(0)}
          >
            1
          </button>
          {pageNumbers[0] > 1 && <span className="px-1">...</span>}
        </>
      )}

      {/* Page Numbers */}
      {pageNumbers.map(page => (
        <button
          key={page}
          className={cn(
            'px-3 py-1 rounded text-sm transition-colors',
            currentPage === page
              ? 'bg-[#1A5EF9] text-white'
              : 'hover:bg-gray-100'
          )}
          onClick={() => onPageChange(page)}
        >
          {page + 1} {/* Display 1-based page number */}
        </button>
      ))}

      {/* Show last page if not in view */}
      {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
        <>
          {pageNumbers[pageNumbers.length - 1] < totalPages - 2 && (
            <span className="px-1">...</span>
          )}
          <button
            className="px-3 py-1 rounded hover:bg-gray-100 text-sm"
            onClick={() => onPageChange(totalPages - 1)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* Next Page Button */}
      <button
        className={cn(
          'p-1 rounded hover:bg-gray-100',
          currentPage >= totalPages - 1 && 'opacity-50 cursor-not-allowed'
        )}
        onClick={() => {
          if (currentPage < totalPages - 1) {
            onPageChange(currentPage + 1);
          }
        }}
        disabled={currentPage >= totalPages - 1}
      >
        <ChevronRight size={20} />
      </button>

      {/* Page info */}
      <span className="ml-2 text-sm text-gray-600">
        Page {currentPage + 1} of {totalPages}
      </span>
    </div>
  );
};

export default PaginationComponent;